import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaPrint, FaWhatsapp } from "react-icons/fa";
import { useCart } from "../context/CartContext";
import logo from "../assets/akkuzeylogo.png";

const WHATSAPP_NUMARA = "905377981129";
const TELEFON = "+90 537 798 11 29";
const EPOSTA = "akkuzey@hotmail.com";
const ADRES = "Çırpıcı Mah. 75/2 Sk. No:109/1, Zeytinburnu / İstanbul";
const KDV_ORANI = 20;

// "1.250,50" / "1250.5" / "1250" → sayı
const sayiyaCevir = (metin) => {
  const temiz = String(metin || "").replace(/\s/g, "");
  if (!temiz) return null;
  const n = Number(temiz.includes(",") ? temiz.replace(/\./g, "").replace(",", ".") : temiz);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * Sepetin yazdırılabilir hali — fiyat teklifi görünümünde bir liste.
 * Müşteri listesini yazdırır / PDF'e kaydeder; mağaza da birim fiyatları girip teklif olarak verebilir.
 * Girilen müşteri bilgisi ve fiyatlar yalnızca bu sayfada kalır, hiçbir yere gönderilmez.
 */
export default function TeklifListesi() {
  const { t, i18n } = useTranslation();
  const { satirlar, bos } = useCart();
  const yerel = i18n.language?.startsWith("tr") ? "tr-TR" : i18n.language || "tr-TR";

  const [musteri, setMusteri] = useState({ ad: "", telefon: "", not: "" });
  const [fiyatlar, setFiyatlar] = useState({});
  const [kdvEkle, setKdvEkle] = useState(false);

  const tarih = useMemo(() => new Date(), []);
  const listeNo = useMemo(() => {
    const p = (n) => String(n).padStart(2, "0");
    return `AKZ-${String(tarih.getFullYear()).slice(2)}${p(tarih.getMonth() + 1)}${p(tarih.getDate())}-${p(tarih.getHours())}${p(tarih.getMinutes())}`;
  }, [tarih]);

  useEffect(() => {
    const onceki = document.title;
    // PDF'e kaydederken dosya adı sekme başlığından gelir
    document.title = `${t("printList.title")} ${listeNo} — Akkuzey Yapı`;
    return () => {
      document.title = onceki;
    };
  }, [t, listeNo]);

  const para = (n) =>
    n.toLocaleString(yerel, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";

  const satirTutari = (s) => {
    const birim = sayiyaCevir(fiyatlar[s.id]);
    return birim == null ? null : birim * s.adet;
  };
  const fiyatGirildi = satirlar.some((s) => satirTutari(s) != null);
  const araToplam = satirlar.reduce((t, s) => t + (satirTutari(s) || 0), 0);
  const kdv = kdvEkle ? (araToplam * KDV_ORANI) / 100 : 0;
  const toplamAdet = satirlar.reduce((t, s) => t + s.adet, 0);

  const whatsappBaglantisi = () => {
    const satirMetni = satirlar.map((s) => `• ${s.adet} x ${s.ad}${s.boyut ? ` (${s.boyut})` : ""}`).join("\n");
    const mesaj = [`${t("printList.title")} ${listeNo}`, "", satirMetni, "", t("shop.whatsappOutro")].join("\n");
    return `https://wa.me/${WHATSAPP_NUMARA}?text=${encodeURIComponent(mesaj)}`;
  };

  if (bos) {
    return (
      <section className="container-page section text-center">
        <h1 className="page-title">{t("printList.title")}</h1>
        <p className="mx-auto mt-4 max-w-md text-[15px] text-muted">{t("printList.empty")}</p>
        <Link to="/urunler" className="btn-primary mt-8 inline-flex">
          {t("product.backToCatalog")}
        </Link>
      </section>
    );
  }

  const alanSinifi =
    "w-full border-0 border-b border-dashed border-line bg-transparent px-0 py-1 text-[14px] text-ink outline-none placeholder:text-muted/70 focus:border-ink";

  return (
    <main className="bg-canvas/50 py-8 sm:py-12">
      <div className="container-page max-w-4xl">
        {/* Ekran araç çubuğu — çıktıda görünmez */}
        <div className="yazdir-gizle mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-semibold tracking-tight text-ink">{t("printList.title")}</h1>
            <p className="mt-1 text-[13.5px] text-muted">{t("printList.hint")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => window.print()} className="btn-primary gap-2">
              <FaPrint size={14} aria-hidden="true" />
              {t("printList.print")}
            </button>
            <a
              href={whatsappBaglantisi()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-[#25D366] px-4 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#1FB955]"
            >
              <FaWhatsapp size={16} aria-hidden="true" />
              {t("printList.whatsapp")}
            </a>
          </div>
        </div>

        {/* Belge */}
        <article className="yazdir-belge rounded-xl border border-line bg-white p-5 shadow-sm sm:p-10">
          {/* Başlık */}
          <div className="flex flex-col gap-6 border-b-2 border-ink pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <img src={logo} alt="Akkuzey Yapı" className="h-14 w-auto object-contain" />
              <div className="text-[12.5px] leading-relaxed text-body">
                <p className="text-[15px] font-semibold text-ink">Akkuzey Yapı Malzemeleri</p>
                <p>{ADRES}</p>
                <p dir="ltr" className="text-start">{TELEFON} · {EPOSTA}</p>
              </div>
            </div>
            <div className="text-start sm:text-end">
              <p className="text-[20px] font-semibold uppercase tracking-wide text-ink">{t("printList.docTitle")}</p>
              <dl className="mt-2 grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-[12.5px] sm:justify-end">
                <dt className="text-muted">{t("printList.no")}</dt>
                <dd className="font-medium tabular-nums text-ink">{listeNo}</dd>
                <dt className="text-muted">{t("printList.date")}</dt>
                <dd className="font-medium tabular-nums text-ink">{tarih.toLocaleDateString(yerel)}</dd>
              </dl>
            </div>
          </div>

          {/* Müşteri */}
          <div className="grid gap-x-6 gap-y-3 border-b border-line py-5 sm:grid-cols-2">
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-eyebrow text-muted">{t("printList.customer")}</span>
              <input
                value={musteri.ad}
                onChange={(e) => setMusteri({ ...musteri, ad: e.target.value })}
                placeholder={t("printList.customerPh")}
                className={alanSinifi}
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-eyebrow text-muted">{t("printList.phone")}</span>
              <input
                value={musteri.telefon}
                onChange={(e) => setMusteri({ ...musteri, telefon: e.target.value })}
                placeholder="05xx xxx xx xx"
                inputMode="tel"
                className={alanSinifi}
              />
            </label>
          </div>

          {/* Ürünler */}
          <div className="-mx-5 overflow-x-auto sm:mx-0">
            <table className="w-full min-w-[640px] border-collapse text-start text-[13px]">
              <thead>
                <tr className="border-b border-line text-[11px] uppercase tracking-eyebrow text-muted">
                  <th className="w-8 py-3 ps-5 text-start font-semibold sm:ps-0">#</th>
                  <th className="py-3 text-start font-semibold">{t("printList.product")}</th>
                  <th className="py-3 text-start font-semibold">{t("printList.option")}</th>
                  <th className="w-16 py-3 text-end font-semibold">{t("printList.qty")}</th>
                  <th className="w-32 py-3 text-end font-semibold">{t("printList.unitPrice")}</th>
                  <th className="w-32 py-3 pe-5 text-end font-semibold sm:pe-0">{t("printList.total")}</th>
                </tr>
              </thead>
              <tbody>
                {satirlar.map((s, i) => {
                  const tutar = satirTutari(s);
                  return (
                    <tr key={s.id} className="break-inside-avoid border-b border-line align-middle">
                      <td className="py-3 ps-5 tabular-nums text-muted sm:ps-0">{i + 1}</td>
                      <td className="py-3 pe-3">
                        <div className="flex items-center gap-3">
                          <img src={s.gorsel} alt="" className="h-11 w-11 shrink-0 rounded border border-line object-contain p-0.5" />
                          <div className="min-w-0">
                            <p className="text-[10.5px] font-semibold uppercase tracking-eyebrow text-muted">{s.marka}</p>
                            <p className="font-medium leading-snug text-ink">{s.ad}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pe-3 text-body">{s.boyut || "—"}</td>
                      <td className="py-3 text-end font-medium tabular-nums text-ink">{s.adet}</td>
                      <td className="py-3 ps-3">
                        <input
                          value={fiyatlar[s.id] || ""}
                          onChange={(e) => setFiyatlar({ ...fiyatlar, [s.id]: e.target.value })}
                          inputMode="decimal"
                          placeholder=" "
                          aria-label={`${t("printList.unitPrice")} — ${s.ad}`}
                          className={`${alanSinifi} text-end tabular-nums`}
                        />
                      </td>
                      <td className="py-3 pe-5 text-end tabular-nums text-ink sm:pe-0">
                        {tutar != null ? para(tutar) : <span className="inline-block w-20 border-b border-dashed border-line">&nbsp;</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Toplamlar */}
          <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <label className="block flex-1 sm:max-w-sm">
              <span className="text-[11px] font-semibold uppercase tracking-eyebrow text-muted">{t("printList.note")}</span>
              <textarea
                value={musteri.not}
                onChange={(e) => setMusteri({ ...musteri, not: e.target.value })}
                rows={2}
                placeholder={t("printList.notePh")}
                className={`${alanSinifi} resize-none`}
              />
            </label>

            <dl className="grid w-full grid-cols-[1fr_auto] gap-x-6 gap-y-1.5 text-[13.5px] sm:w-72">
              <dt className="text-muted">{t("printList.totalQty")}</dt>
              <dd className="text-end font-medium tabular-nums text-ink">{toplamAdet}</dd>
              {fiyatGirildi && (
                <>
                  <dt className="text-muted">{t("printList.subtotal")}</dt>
                  <dd className="text-end tabular-nums text-ink">{para(araToplam)}</dd>
                  {kdvEkle && (
                    <>
                      <dt className="text-muted">{t("printList.vat", { oran: KDV_ORANI })}</dt>
                      <dd className="text-end tabular-nums text-ink">{para(kdv)}</dd>
                    </>
                  )}
                  <dt className="border-t border-ink pt-2 font-semibold text-ink">{t("printList.grandTotal")}</dt>
                  <dd className="border-t border-ink pt-2 text-end font-semibold tabular-nums text-ink">{para(araToplam + kdv)}</dd>
                </>
              )}
            </dl>
          </div>

          {fiyatGirildi && (
            <label className="yazdir-gizle mt-3 flex cursor-pointer items-center justify-end gap-2 text-[13px] text-body">
              <input type="checkbox" checked={kdvEkle} onChange={(e) => setKdvEkle(e.target.checked)} className="h-4 w-4 accent-ink" />
              {t("printList.addVat", { oran: KDV_ORANI })}
            </label>
          )}

          <p className="mt-8 border-t border-line pt-4 text-[11.5px] leading-relaxed text-muted">
            {t("printList.footer", { telefon: TELEFON })}
          </p>
        </article>

        <div className="yazdir-gizle mt-6 flex justify-center">
          <Link to="/urunler" className="link-arrow text-[13.5px]">
            {t("printList.continue")}
            <span aria-hidden="true" className="arrow-forward" />
          </Link>
        </div>
      </div>
    </main>
  );
}
